/**
 * Quality report — code quality vs budget spend.
 *
 * Fully redesigned: KPI band, sprint score trend, AI insights and the
 * full per-member breakdown. Self-contained styling (dashboard-pages.css).
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  Award,
  Bug,
  Crown,
  GitPullRequest,
  Plus,
  ShieldCheck,
  Sparkles,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { apiGet, apiPost } from '../api';
import { useTeamDashboard } from '../TeamDashboardContext';
import './dashboard-pages.css';

const T = {
  accent: '#6e6bff',
  accent2: '#a855f7',
  cyan: '#22d3ee',
  green: '#34d399',
  amber: '#fbbf24',
  red: '#fb4d6d',
};

const FONT_ID = 'tdp-fonts';
function ensureFonts() {
  if (document.getElementById(FONT_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

const Card: React.FC<{ children: React.ReactNode; className?: string; delay?: number; style?: React.CSSProperties }> = ({ children, className = '', delay = 0, style }) => (
  <motion.section
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className={`tdp-card ${className}`}
    style={style}
  >
    {children}
  </motion.section>
);

const SectionTitle: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <div className="tdp-section-title">
    <span>{children}</span>
    {right}
  </div>
);

const DEMO_ROWS = [
  { initials: 'AK', name: 'Arjun K.', role: 'owner', spent: 6250, prs: 3, score: 88, bugs: 0 },
  { initials: 'SR', name: 'Sneha R.', role: 'lead', spent: 7200, prs: 2, score: 71, bugs: 1 },
  { initials: 'RM', name: 'Rahul M.', role: 'dev', spent: 4000, prs: 1, score: 44, bugs: 3 },
  { initials: 'PK', name: 'Priya K.', role: 'dev', spent: 2000, prs: 2, score: 91, bugs: 0 },
];

const SPRINT_SCORES = [68, 72, 65, 70, 74, 76];

function verdictFor(score: number): [string, string] {
  if (score >= 80) return ['high quality', T.green];
  if (score >= 60) return ['review', T.amber];
  return ['at risk', T.red];
}

export default function QualityPage() {
  const { showToast, isReal, members, teamSlug, currentMemberInitials, theme } = useTeamDashboard();
  const [qualityRows, setQualityRows] = useState<Array<Record<string, unknown>>>([]);
  const [relationData, setRelationData] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    ensureFonts();
  }, []);

  useEffect(() => {
    if (!isReal) return;
    let cancelled = false;
    async function loadRealQuality() {
      try {
        const [qualityRes, relationRes] = await Promise.all([
          apiGet<{ data?: Array<Record<string, unknown>> }>(`/${teamSlug}/quality`),
          apiGet<{ data?: Record<string, unknown> }>(`/${teamSlug}/relationship-health`),
        ]);
        if (!cancelled) {
          setQualityRows(Array.isArray(qualityRes?.data) ? qualityRes.data : []);
          setRelationData(relationRes?.data && typeof relationRes.data === 'object' ? relationRes.data : null);
        }
      } catch {
        if (!cancelled) {
          setQualityRows([]);
          setRelationData(null);
        }
      }
    }
    void loadRealQuality();
    const iv = setInterval(() => void loadRealQuality(), 45_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [isReal, teamSlug]);

  const rows = isReal && members.length > 0
    ? members.map((m) => ({
        initials: m.initials,
        name: m.name,
        role: m.role,
        spent: Math.round(Number(m.totalSpent || 0)),
        prs: Number(m.prsOpen || 0),
        score: Math.round(Number(m.qualityScore || 0)),
        bugs: 0,
      }))
    : DEMO_ROWS;

  const trendValues = (isReal
    ? qualityRows.slice(0, 6).map((row) => Math.round(Number(row.score || 0))).filter((v) => Number.isFinite(v))
    : SPRINT_SCORES
  ).reverse();
  const safeTrend = trendValues.length > 0 ? trendValues : SPRINT_SCORES;
  const maxScore = Math.max(...safeTrend, 1);

  const avgScore = Math.round(rows.reduce((a, r) => a + r.score, 0) / Math.max(rows.length, 1));
  const totalPrs = rows.reduce((a, r) => a + r.prs, 0);
  const totalBugs = rows.reduce((a, r) => a + r.bugs, 0);
  const topPerformers = rows.filter((r) => r.score >= 80);

  async function handleLogBug() {
    if (!isReal) {
      showToast('Bug logged', 'Saved for demo workflow', 'success');
      return;
    }
    const currentMember = members.find((m) => m.initials === currentMemberInitials) || members[0];
    if (!currentMember) {
      showToast('Log bug', 'No member found to attach bug log', 'error');
      return;
    }
    try {
      await apiPost(`/${teamSlug}/activity`, {
        memberId: currentMember.id,
        action: 'logged quality bug',
        detail: 'Quality issue was logged from Quality Report panel',
        cost: 0,
        type: 'review',
      });
      showToast('Bug logged', 'Quality bug added to activity feed', 'success');
    } catch {
      showToast('Log bug', 'Could not log bug in real mode', 'error');
    }
  }

  const isDark = theme === 'dark';

  return (
    <div data-theme={isDark ? 'dark' : 'light'} className="tdp" style={{ minHeight: '100%' }}>
      <div className="tdp-wrap">
        {/* hero */}
        <div className="tdp-hero">
          <div className="tdp-hero-row">
            <div className="tdp-hero-main">
              <div className="tdp-eyebrow">
                <ShieldCheck size={11} />
                Workspace · quality
              </div>
              <h1 className="tdp-title">Quality report</h1>
              <p className="tdp-sub">Code quality vs budget spend · Sprint 4</p>
            </div>
            <button className="tdp-btn tdp-btn-primary" onClick={() => void handleLogBug()}>
              <Plus size={13} /> Log bug
            </button>
          </div>
        </div>

        {/* KPI band */}
        <div className="tdp-kpi-grid">
          {[
            { label: 'Avg score', value: `${avgScore}`, suffix: '/100', sub: '+4 pts', up: true, color: avgScore >= 75 ? T.green : T.amber, icon: ShieldCheck },
            { label: 'Total PRs', value: String(totalPrs), suffix: '', sub: '+3 this week', up: true, color: T.accent, icon: GitPullRequest },
            { label: 'Critical bugs', value: String(totalBugs), suffix: '', sub: totalBugs > 0 ? 'needs attention' : 'all clear', up: totalBugs === 0, color: totalBugs > 0 ? T.red : T.green, icon: Bug },
            { label: 'Top performers', value: String(topPerformers.length), suffix: '', sub: topPerformers.map((p) => p.name.split(' ')[0]).join(', ') || '—', up: true, color: T.amber, icon: Crown },
          ].map((kpi, i) => (
            <Card key={kpi.label} delay={0.05 + i * 0.05} className="tdp-kpi">
              <span className="tdp-kpi-icon" style={{ background: `${kpi.color}18`, color: kpi.color }}>
                <kpi.icon size={14} />
              </span>
              <div className="tdp-kpi-value">
                {kpi.value}
                {kpi.suffix && <span style={{ fontSize: 12, color: 'var(--tdp-faint)', fontWeight: 600 }}>{kpi.suffix}</span>}
              </div>
              <div className="tdp-kpi-label">{kpi.label}</div>
              <div className="tdp-kpi-sub" style={{ color: kpi.color, display: 'flex', alignItems: 'center', gap: 3 }}>
                {kpi.up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                {kpi.sub}
              </div>
            </Card>
          ))}
        </div>

        {/* trend + insights */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 18, marginBottom: 22 }} className="tdp-quality-grid">
          <style>{`@media (min-width: 1080px) { .tdp-quality-grid { grid-template-columns: minmax(0,1.4fr) minmax(0,1fr) !important; } }`}</style>

          <div className="tdp-section" style={{ marginBottom: 0 }}>
            <SectionTitle right={<span className="tdp-mono" style={{ fontSize: 9, color: 'var(--tdp-faint)' }}>last 6 sprints</span>}>
              Score trend
            </SectionTitle>
            <Card delay={0.16} style={{ padding: '16px 18px 12px' }}>
              <div className="tdp-trend">
                {safeTrend.map((score, i) => {
                  const isLatest = i === safeTrend.length - 1;
                  const color = score >= 75 ? T.green : score >= 60 ? T.amber : T.red;
                  return (
                    <div key={i} className="tdp-trend-col">
                      <span className="tdp-trend-val" style={{ color: isLatest ? color : undefined, fontWeight: isLatest ? 700 : 400 }}>
                        {score}
                      </span>
                      <motion.div
                        className="tdp-trend-bar"
                        style={{ background: isLatest ? `linear-gradient(180deg, ${color}, ${color}99)` : undefined }}
                        initial={{ height: 0 }}
                        animate={{ height: `${(score / maxScore) * 72}px` }}
                        transition={{ delay: 0.25 + i * 0.06, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      />
                      <span className="tdp-trend-val" style={{ color: isLatest ? color : undefined }}>
                        {isLatest ? 'Now' : `S${i + 1}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          <div className="tdp-section" style={{ marginBottom: 0 }}>
            <SectionTitle right={<Sparkles size={10} style={{ color: 'var(--tdp-accent)' }} />}>AI insights</SectionTitle>
            <Card delay={0.2}>
              {isReal && relationData ? (
                <>
                  <div className="tdp-insight">
                    <div className="tdp-insight-label" style={{ color: T.green }}>Relationship score</div>
                    <div className="tdp-insight-text">
                      {String(relationData.score || '-')}/100 · {String(relationData.trend || 'stable')}
                    </div>
                  </div>
                  <div className="tdp-insight">
                    <div className="tdp-insight-label" style={{ color: T.amber }}>Summary</div>
                    <div className="tdp-insight-text">{String(relationData.summary || 'No summary yet')}</div>
                  </div>
                  <div className="tdp-insight">
                    <div className="tdp-insight-label" style={{ color: T.red }}>Reason</div>
                    <div className="tdp-insight-text">{String(relationData.reason || 'No reason yet')}</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="tdp-insight">
                    <div className="tdp-insight-label" style={{ color: T.green }}>Top performer</div>
                    <div className="tdp-insight-text">Priya shipped 2 PRs avg 91/100, zero critical bugs.</div>
                  </div>
                  <div className="tdp-insight">
                    <div className="tdp-insight-label" style={{ color: T.red }}>Needs attention</div>
                    <div className="tdp-insight-text">Rahul has 44/100 score with 3 critical bugs — review before merge.</div>
                  </div>
                  <div className="tdp-insight">
                    <div className="tdp-insight-label" style={{ color: T.amber }}>Best value</div>
                    <div className="tdp-insight-text">Arjun: ₹6,250 spent at 88/100 — highest quality-per-rupee.</div>
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>

        {/* full breakdown */}
        <div className="tdp-section">
          <SectionTitle right={<Award size={11} style={{ color: 'var(--tdp-faint)' }} />}>Full breakdown</SectionTitle>
          <Card delay={0.24}>
            <div className="tdp-table-head" style={{ gridTemplateColumns: 'minmax(0,1.8fr) repeat(4,minmax(0,0.7fr)) minmax(0,1fr)' }}>
              <span>Member</span>
              <span style={{ textAlign: 'right' }}>Spent</span>
              <span style={{ textAlign: 'right' }}>PRs</span>
              <span style={{ textAlign: 'right' }}>Score</span>
              <span style={{ textAlign: 'right' }}>Bugs</span>
              <span style={{ textAlign: 'right' }}>Verdict</span>
            </div>
            {rows.map((r, i) => {
              const [verdict, color] = verdictFor(r.score);
              const bugColor = r.bugs === 0 ? T.green : r.bugs >= 3 ? T.red : T.amber;
              return (
                <motion.div
                  key={r.initials}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.28 + i * 0.05 }}
                  className="tdp-table-row"
                  style={{ gridTemplateColumns: 'minmax(0,1.8fr) repeat(4,minmax(0,0.7fr)) minmax(0,1fr)' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
                    <span className="tdp-avatar" style={{ width: 26, height: 26, fontSize: 8.5, background: `linear-gradient(135deg, ${T.accent}, ${T.accent2})` }}>
                      {r.initials}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tdp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.name}
                      </div>
                      <div className="tdp-feed-meta" style={{ textTransform: 'capitalize' }}>{r.role}</div>
                    </div>
                  </div>
                  <span className="tdp-table-num">₹{r.spent.toLocaleString()}</span>
                  <span className="tdp-table-num">{r.prs}</span>
                  <span className="tdp-table-num" style={{ color, fontWeight: 700 }}>
                    {r.score}
                  </span>
                  <span className="tdp-table-num" style={{ color: bugColor, fontWeight: 700 }}>
                    {r.bugs}
                  </span>
                  <span style={{ textAlign: 'right' }}>
                    <span className="tdp-pill" style={{ background: `${color}1a`, color }}>
                      {verdict}
                    </span>
                  </span>
                </motion.div>
              );
            })}
            {rows.length === 0 && (
              <div className="tdp-feed-meta" style={{ padding: 22, textAlign: 'center' }}>
                No quality records yet — add team activity and commits to populate the report.
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
